import { FileText } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { Document, Page, pdfjs } from "react-pdf/dist/esm/entry.webpack";

// Configurar worker do PDF.js
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

interface DocumentItemProps {
  fileBlob: Blob | null;
  isLoading?: boolean;
  rotation?: number;
}

export const DocumentItem = ({
  fileBlob,
  isLoading = false,
  rotation = 0,
}: DocumentItemProps) => {
  const [fileUrl, setFileUrl] = useState<string>("");
  const [fileType, setFileType] = useState<string>("");
  const [numPages, setNumPages] = useState<number>(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const imageRef = useRef<HTMLImageElement>(null);

  // Detectar tipo de arquivo e criar object URL
  useEffect(() => {
    if (fileBlob) {
      // Detectar tipo baseado no MIME type do blob
      const mimeType = fileBlob.type;
      setFileType(mimeType);

      // Criar object URL para exibição
      const url = URL.createObjectURL(fileBlob);
      setFileUrl(url);

      // Cleanup function
      return () => {
        URL.revokeObjectURL(url);
      };
    }

    setFileUrl("");
    setFileType("");
    return undefined;
  }, [fileBlob]);

  const onDocumentLoadSuccess = ({
    numPages: totalPages,
  }: {
    numPages: number;
  }) => {
    setNumPages(totalPages);
  };

  // Funções de zoom para imagem
  const handleImageMouseMove = (e: React.MouseEvent<HTMLImageElement>) => {
    if (!imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    setMousePosition({ x, y });
  };

  const handleImageMouseEnter = () => {
    setIsZoomed(true);
  };

  const handleImageMouseLeave = () => {
    setIsZoomed(false);
  };

  // Verificar se é PDF baseado no MIME type
  const isPdf = fileType === "application/pdf";

  const renderPdf = () => (
    <div style={{ width: "100%", maxWidth: "1000px", margin: "0 auto" }}>
      <div
        style={{
          maxHeight: "70vh",
          overflowY: "auto",
          padding: "20px",
          background: "white",
          borderRadius: "8px",
          border: "1px solid #ddd",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <Document file={fileUrl} onLoadSuccess={onDocumentLoadSuccess}>
            {Array.from(new Array(numPages), (el, pageIndex) => (
              <div
                key={`page_${pageIndex + 1}`}
                style={{
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  borderRadius: "4px",
                }}
              >
                <Page pageNumber={pageIndex + 1} width={600} />
              </div>
            ))}
          </Document>
        </div>
      </div>
    </div>
  );

  return (
    <div className="document-item-style">
      <div
        className="file-preview"
        style={{
          transform: `rotate(${rotation}deg)`,
          transition: "transform 0.2s ease-out",
        }}
      >
        {isLoading && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "200px",
            }}
          >
            <p>Carregando arquivo...</p>
          </div>
        )}

        {!isLoading && fileBlob && fileUrl && isPdf && renderPdf()}

        {!isLoading && fileBlob && fileUrl && !isPdf && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              maxWidth: "100%",
              overflow: "hidden",
            }}
          >
            <img
              ref={imageRef}
              src={fileUrl}
              alt=""
              onMouseMove={handleImageMouseMove}
              onMouseEnter={handleImageMouseEnter}
              onMouseLeave={handleImageMouseLeave}
              style={{
                maxWidth: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                transform: isZoomed ? "scale(2)" : "scale(1)",
                transformOrigin: `${mousePosition.x}% ${mousePosition.y}%`,
                transition: "transform 0.2s ease-out",
                cursor: "zoom-in",
              }}
            />
          </div>
        )}

        {!isLoading && (!fileBlob || !fileUrl) && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "200px",
              color: "var(--color-text-light)",
            }}
          >
            <FileText size={25} />
            <p style={{ marginTop: "10px", fontSize: "14px" }}>
              Área de Visualização
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
